import React, { Fragment, useState } from 'react';
import {
  getMinMaxSelectedMeals,
  getOrderForm,
  getSelectedDays,
} from '../../../../redux/selectors/orderFormSelector';
import { DateTime } from 'luxon';
import { faCheck, faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffectOnce } from 'react-use';
import { updateCustomDaysSideOrders } from '../../../../redux/actions/orderFormActions';
import { nanoid } from '@reduxjs/toolkit';
import {
  clearSideOrderError,
  updateSideOrder,
} from '../../../../features/chooseSideOrders/chooseSideOrdersSlice';
import CustomModalTS from '../../../common/CustomModalTS';
import DaysAmount from '../../../common/daysAmount';
import { capitalizeFirstLetter } from '../../../../salesManago/helpers';
import Button from '../../../common/Button';
import Banner from '../../../common/Banner';
import { declination, parsePrice } from '../../../../utils';
import EmptyImageSideOrder from '../../../../assets/icons/EmptyImageSideOrder';
import { SideOrderT } from '../../../../@types/sideOrder.type';
import { useAppDispatch, useAppSelector } from '../../../../config/hooks';
import {
  calculateSummary,
  decreaseAllSideOrders,
  decreaseSideOrdersItem,
  increaseAllSideOrders,
  increaseSideOrdersItem,
} from '../helpers/sideOrdersHelpers';
import { getChooseSideOrdersError } from '../../../../features/chooseSideOrders/chooseSideOrdersSelectors';

type Props = {
  isOpen: boolean;
  selectedSideOrder: SideOrderT;
  setIsOpen: (b: boolean) => void;
  setSelectedSideOrder: (p: null | SideOrderT) => void;
  chooseSideOrdersList: Record<string, Record<number, SideOrderT>>;
};

const SelectSideOrdersModal = ({
  chooseSideOrdersList,
  isOpen,
  selectedSideOrder,
  setIsOpen,
  setSelectedSideOrder,
}: Props) => {
  const dispatch = useAppDispatch();
  const selectedDays = useAppSelector(getSelectedDays);
  const { selectedMeals } = useAppSelector(getOrderForm) || {};
  const { max: mealsLimit } = useAppSelector(getMinMaxSelectedMeals);
  const error = useAppSelector(getChooseSideOrdersError);

  const selectedMealsLength = selectedMeals.length;

  const [allSideOrdersAmount, setAllSideOrdersAmount] = useState(0);

  const [sideOrdersState, setSideOrdersState] = useState<
    Record<string, Record<string, SideOrderT>>
  >({});

  useEffectOnce(() => {
    setSideOrdersState(chooseSideOrdersList);
  });

  const sortedDays = [...selectedDays].sort();

  const {
    defaultPrice,
    imageUrl,
    limitedByMaximumSelectedMeals,
    maxQuantity,
    minQuantity,
    name,
    possibleSideOrderId,
  } = selectedSideOrder || {};

  const closeModal = () => {
    setSelectedSideOrder(null);
    setIsOpen(false);
    setAllSideOrdersAmount(0);
    dispatch(clearSideOrderError());
  };

  const { days, numberOfSideOrders, priceOfSideOrders } = calculateSummary(
    sideOrdersState,
    possibleSideOrderId
  );

  const monthsDictionary: Record<number, string> = {
    1: 'stycznia',
    2: 'lutego',
    3: 'marca',
    4: 'kwietnia',
    5: 'maja',
    6: 'czerwca',
    7: 'lipca',
    8: 'sierpnia',
    9: 'września',
    10: 'października',
    11: 'listopada',
    12: 'grudnia',
  };

  return (
    <CustomModalTS
      childrenContainerClass="custom-modal-side-orders__container"
      className="custom-modal-side-orders"
      isModalOpen={isOpen}
      setIsModalOpen={setIsOpen}
    >
      <div className="h100 custom-modal-side-orders__top-wrapper">
        <FontAwesomeIcon
          className="custom-modal-side-orders__close cursor-pointer"
          icon={faTimes}
          onClick={() => {
            closeModal();
          }}
        />
        <h6 className="label-l">Wybierz dni dodatku:</h6>
        <div className="display-flex align-items-center">
          {imageUrl ? (
            <img
              alt={name}
              className="border-radius-default object-fit-contain"
              height={80}
              src={imageUrl}
              width={80}
            />
          ) : (
            <EmptyImageSideOrder />
          )}
          <div className="spacer-left-16">
            <p className="label-m spacer-bottom-8">{name}</p>
            <p className="label-m color-primary">
              {parsePrice(defaultPrice)} zł / dzień
            </p>
          </div>
        </div>
      </div>
      <div className="custom-modal-side-orders__center-wrapper">
        <div className="custom-modal-side-orders__center-wrapper-day custom-modal-side-orders__center-wrapper-day--all-days">
          <p className="label-s">Ustaw wszystkie dni na x sztuk:</p>
          <div className="custom-modal-side-orders__center-wrapper-day-amount">
            <DaysAmount
              days={allSideOrdersAmount}
              decrease={() => {
                dispatch(
                  decreaseAllSideOrders(
                    allSideOrdersAmount,
                    setSideOrdersState,
                    sideOrdersState,
                    possibleSideOrderId,
                    setAllSideOrdersAmount,
                    minQuantity
                  )
                );
              }}
              disableDecrease={allSideOrdersAmount === 0}
              increase={() => {
                dispatch(
                  increaseAllSideOrders(
                    allSideOrdersAmount,
                    setSideOrdersState,
                    sideOrdersState,
                    possibleSideOrderId,
                    setAllSideOrdersAmount,
                    maxQuantity,
                    minQuantity,
                    limitedByMaximumSelectedMeals,
                    selectedMealsLength,
                    mealsLimit,
                    chooseSideOrdersList
                  )
                );
              }}
              withoutBorders
            />
          </div>
        </div>

        {error === 'maxMeals' && (
          <Banner
            className="spacer-top-8"
            error
            onClose={() => {
              dispatch(clearSideOrderError());
            }}
            text={`Nie możesz dodać więcej sztuk tego dodatku ponieważ z uwagi na gabaryt jest on liczony jako kolejny posiłek. Limit posiłków w wybranym przez Ciebie wariancie diety to ${mealsLimit}.`}
          />
        )}

        {error === 'maxSideOrders' && (
          <Banner
            className="spacer-top-8"
            error
            onClose={() => {
              dispatch(clearSideOrderError());
            }}
            text={`Możesz dodać maksymalnie ${declination(
              maxQuantity,
              'dodatek',
              'dodatki',
              'dodatków'
            )} na każdy dzień. Jeśli chcesz dodać więcej dodatków, musisz złożyć nowe zamówienie.`}
          />
        )}

        {error === 'minMeals' && (
          <Banner
            className="spacer-top-8"
            error
            onClose={() => {
              dispatch(clearSideOrderError());
            }}
            text={`Minimalna ilość dodania to ${minQuantity}. Wartość ta przekracza limit który wynosi ${mealsLimit} razem z wybranymi wcześniej posiłkami`}
          />
        )}

        {!!Object.values(sideOrdersState).length &&
          sortedDays.map((day: number) => {
            const dateTime = DateTime.fromMillis(day).setLocale('pl-PL');
            const parseDay = `${capitalizeFirstLetter(dateTime.weekdayLong)}, ${
              dateTime.day
            } ${monthsDictionary[dateTime.month]}`;

            const isoDay = DateTime.fromMillis(day).toISODate() as string;

            const amount =
              possibleSideOrderId &&
              sideOrdersState[isoDay]?.[possibleSideOrderId]?.amount;

            return (
              <Fragment key={nanoid()}>
                <div className="custom-modal-side-orders__center-wrapper-day">
                  <p
                    className={`display-flex ${
                      !!amount && amount > 0 ? 'label-s' : 'body-s'
                    }`}
                  >
                    {!!amount && amount > 0 && (
                      <FontAwesomeIcon
                        className="font-size-20 spacer-right-10"
                        icon={faCheck}
                      />
                    )}

                    {parseDay}
                  </p>
                  <div className="custom-modal-side-orders__center-wrapper-day-amount">
                    <DaysAmount
                      days={amount || 0}
                      decrease={() => {
                        dispatch(
                          decreaseSideOrdersItem(
                            amount,
                            isoDay,
                            setSideOrdersState,
                            sideOrdersState,
                            possibleSideOrderId,
                            minQuantity
                          )
                        );
                        setAllSideOrdersAmount(0);
                      }}
                      disableDecrease={amount === 0}
                      increase={() => {
                        dispatch(
                          increaseSideOrdersItem(
                            amount,
                            isoDay,
                            setSideOrdersState,
                            sideOrdersState,
                            possibleSideOrderId,
                            maxQuantity,
                            minQuantity,
                            limitedByMaximumSelectedMeals,
                            selectedMealsLength,
                            mealsLimit,
                            setAllSideOrdersAmount,
                            chooseSideOrdersList
                          )
                        );
                      }}
                      withoutBorders
                    />
                  </div>
                </div>
                {error === `maxSingleMeals-${isoDay}` && (
                  <Banner
                    error
                    onClose={() => {
                      dispatch(clearSideOrderError());
                    }}
                    text={`Nie możesz dodać więcej sztuk tego dodatku ponieważ z uwagi na gabaryt jest on liczony jako kolejny posiłek. Limit posiłków w wybranym przez Ciebie wariancie diety to ${mealsLimit}.`}
                  />
                )}

                {error === `maxSingleSideOrder-${isoDay}` && (
                  <Banner
                    error
                    onClose={() => {
                      dispatch(clearSideOrderError());
                    }}
                    text={`Możesz dodać maksymalnie ${declination(
                      maxQuantity,
                      'dodatek',
                      'dodatki',
                      'dodatków'
                    )} na każdy dzień. Jeśli chcesz dodać więcej dodatków, musisz złożyć nowe zamówienie.`}
                  />
                )}

                {error === `minSingleMeals-${isoDay}` && (
                  <Banner
                    className="spacer-top-8"
                    error
                    onClose={() => {
                      dispatch(clearSideOrderError());
                    }}
                    text={`Minimalna ilość dodania to ${minQuantity}. Wartość ta przekracza limit który wynosi ${mealsLimit} razem z wybranymi wcześniej posiłkami`}
                  />
                )}
              </Fragment>
            );
          })}
      </div>
      <div className="custom-modal-side-orders__bottom-wrapper">
        <div>
          <p className="label-l color-success spacer-bottom-4">
            Cena razem: {parsePrice(priceOfSideOrders)} zł
          </p>
          <p className="body-s color-gray-600 spacer-all-0">
            Wybrano:{' '}
            {declination(numberOfSideOrders, 'dodatek', 'dodatki', 'dodatków')}{' '}
            na {declination(days, 'dzień', 'dni', 'dni')}
          </p>
        </div>
        <Button
          colorPrimary
          label="Dodaj"
          onClick={() => {
            dispatch(updateCustomDaysSideOrders(sideOrdersState));
            dispatch(updateSideOrder(sideOrdersState));
            closeModal();
          }}
          sizeMedium
        />
      </div>
    </CustomModalTS>
  );
};

export default SelectSideOrdersModal;
